import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { useRpc } from '../data/people.ts'
import { useWriteAccess } from '../ui/hooks.ts'
import {
  backupOverdue, barPercent, BACKUP_OVERDUE_HOURS, deviceName, DIAGNOSTICS_SHOWN, durationParts, foldDevices, listCut, OUTCOME_ORDER, problemRows, SKEW_BUCKETS, sortDevices, statusCount,
  takenPercent,
  type CommandWindow, type HealthDevice, type SyncHealth,
} from '../data/syncHealth.ts'
import { fmtNum } from '../lib/format.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, NotAllowed, Pill, StatusPill } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'

type T = (k: string, o?: Record<string, unknown>) => string
const dur = (t: T, ms: number | null | undefined, lang: string): string => {
  if (ms === null || ms === undefined) return '—'
  const p = durationParts(ms)
  return t(`syncHealth.dur.${p.unit}`, { n: fmtNum(p.n, lang, 1) })
}
const commandLabel = (t: T, name: string): string => t(`history.action.${name.replace(/\./g, '_')}`, { defaultValue: name })

/** "Santé de la synchro" (ARCHITECTURE 17.1): how the club's devices and their commands are doing, for the people who hold audit.read on the site (the direction).
 *  Online only (api.sync_health), read when the page opens and on demand; nothing of it is kept on the device. Commands over 24 h or 7 days by outcome and by name, how
 *  long they took to arrive, the devices' clocks, every device with what it last said (work waiting for more than 24 h stands out), what went wrong on the devices
 *  (scrubbed, kept for the club's retention period), the scheduled jobs that failed, and the age of the last backup. When the server cut a list (200 devices, 50
 *  diagnostics, 20 failed jobs), the page says how many are shown of how many.
 *  M23 (docs/M23.md items 8, 9 and 24): whoever lacks audit.read gets the "Vous n'avez pas accès" card instead of a page the server refuses (cosmetic: the
 *  server decides); offline the page says so and never "could not be loaded"; a failure's code waits behind "Détails", never a raw text on the page. */
export function SyncHealthPage() {
  const { t, i18n } = useTranslation()
  const { org, site, deviceId } = useClub()
  const access = useWriteAccess('audit.read')
  const health = useRpc<SyncHealth>('sync_health', { p_org: org.org_id, p_site: site.site_id }, access.permitted)
  const [win, setWin] = useState<'h24' | 'd7'>('h24')
  const [busy, setBusy] = useState(false)
  const [allDevices, setAllDevices] = useState(false)
  const [allDiag, setAllDiag] = useState(false)
  const tz = site.timezone
  const lang = i18n.language
  const h = health.data
  const offline = !health.online || health.error === 'OFFLINE'
  const error = health.error && health.error !== 'OFFLINE' ? health.error : null
  const devicesCut = h ? listCut(h.devices.length, h.devices_total) : null
  const diagCut = h ? listCut(h.diagnostics.last.length, h.diagnostics.total) : null
  const cronCut = h ? listCut(h.cron_failures.length, h.cron_failures_total) : null

  async function refresh() { setBusy(true); try { await health.reload() } finally { setBusy(false) } }

  if (!access.permitted) return <NotAllowed permission="audit.read" />
  return (
    <section className="flex flex-col gap-5" data-testid="sync-health-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="sync" className="size-7 text-brand dark:text-fairway" />{t('syncHealth.title')}</h1>
      <p className="meta">{t('syncHealth.help', { site: site.name })}</p>
      <button className="btn" onClick={() => void refresh()} disabled={busy || offline} data-testid="health-refresh">{busy ? t('common.loading') : t('syncHealth.refresh')}</button>
      {offline && <Banner tone="warn" testId="health-offline">{t('syncHealth.offline')}</Banner>}
      {error && (
        <Banner tone="error">
          <span data-testid="health-error">{error === 'PERMISSION_DENIED' || error === 'SCOPE_DENIED' ? t('syncHealth.denied') : t('syncHealth.failed')}</span>
          {error !== 'PERMISSION_DENIED' && error !== 'SCOPE_DENIED' && (
            <details className="mt-1"><summary className="flex min-h-14 cursor-pointer list-none items-center font-semibold [&::-webkit-details-marker]:hidden">{t('syncHealth.errorDetails')}</summary><code className="break-all text-sm" data-testid="health-error-code">{error}</code></details>
          )}
        </Banner>
      )}
      {!h ? (!offline && !error && <p className="meta">{t('common.loading')}</p>) : (
        <>
          <p className="meta" data-testid="health-generated">{t('syncHealth.generated', { at: formatLocal(h.generated_at, tz, 'dd/MM/yyyy HH:mm:ss') })}</p>

          {/* the commands */}
          <div className="card flex flex-col gap-3" data-testid="health-commands">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold">{t('syncHealth.commands.title')}</h2>
              <div className="flex gap-2" role="group" aria-label={t('syncHealth.commands.window')}>
                {(['h24', 'd7'] as const).map((w) => (
                  <button key={w} className={`btn ${win === w ? 'btn-primary' : ''}`} aria-pressed={win === w} onClick={() => setWin(w)} data-testid={`health-window-${w}`}>{t(`syncHealth.commands.${w}`)}</button>
                ))}
              </div>
            </div>
            <CommandsBlock w={h.commands[win]} t={t} lang={lang} />
          </div>

          {/* the clocks */}
          <div className="card flex flex-col gap-2" data-testid="health-skew">
            <h2 className="text-lg font-bold">{t('syncHealth.skew.title')}</h2>
            <p className="meta">{t('syncHealth.skew.help')}</p>
            {(() => {
              const max = Math.max(0, ...h.clock_skew.map((b) => b.n))
              return SKEW_BUCKETS.map((k) => {
                const n = h.clock_skew.find((b) => b.bucket === k)?.n ?? 0
                return (
                  <div key={k} className="flex items-center gap-3 text-sm">
                    <span className="w-28 shrink-0">{t(`syncHealth.skew.${k}`)}</span>
                    <span className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-night"><span className={`block h-full rounded-full ${k === 'lt_2s' ? 'bg-fairway' : k === 'unknown' ? 'bg-slate-400 dark:bg-slate-500' : 'bg-amber-500'}`} style={{ width: `${barPercent(n, max)}%` }} /></span>
                    <span className="w-12 shrink-0 text-right tabular-nums">{n}</span>
                  </div>
                )
              })
            })()}
          </div>

          {/* the devices */}
          <div className="flex flex-col gap-2" data-testid="health-devices">
            <h2 className="text-lg font-bold">{t('syncHealth.devices.title')}</h2>
            {h.app_versions.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="health-versions">
                {h.app_versions.map((v) => <Pill key={v.app_version ?? '-'} tone="gray">{t('syncHealth.devices.version', { version: v.app_version ?? '?', count: v.devices })}</Pill>)}
              </div>
            )}
            {h.devices.length === 0 && <p className="meta">{t('syncHealth.devices.none')}</p>}
            {devicesCut && <p className="meta" data-testid="health-devices-cut">{t('syncHealth.devices.cut', devicesCut)}</p>}
            {(() => {
              const sorted = sortDevices(h.devices, deviceId)
              const shown = foldDevices(sorted, allDevices)
              return (
                <>
                  {shown.map((d) => <DeviceCard key={d.device_id} d={d} mine={d.device_id === deviceId} t={t} lang={lang} tz={tz} />)}
                  {(allDevices || shown.length < sorted.length) && (
                    <button className="btn" onClick={() => setAllDevices(!allDevices)} data-testid="health-devices-all">{allDevices ? t('syncHealth.showLess') : t('syncHealth.devices.showAll', { count: sorted.length })}</button>
                  )}
                </>
              )
            })()}
          </div>

          {/* what went wrong on the devices */}
          <div className="flex flex-col gap-2" data-testid="health-diagnostics">
            <h2 className="text-lg font-bold">{t('syncHealth.diag.title')}</h2>
            <p className="meta">{t('syncHealth.diag.help')} {t('syncHealth.diag.kept', { count: h.diagnostics.kept_days })}</p>
            {diagCut && <p className="meta" data-testid="health-diag-cut">{t('syncHealth.diag.cut', diagCut)}</p>}
            {h.diagnostics.by_code_d7.length > 0 && (
              <div className="flex flex-wrap gap-2">{h.diagnostics.by_code_d7.map((c) => <Pill key={c.code} tone="amber">{c.code} · {c.n}</Pill>)}</div>
            )}
            {h.diagnostics.last.length === 0 && <p className="meta" data-testid="health-diag-none">{t('syncHealth.diag.none')}</p>}
            <ul className="flex flex-col gap-2">
              {(allDiag ? h.diagnostics.last : h.diagnostics.last.slice(0, DIAGNOSTICS_SHOWN)).map((x) => (
                <li key={x.id} className="card flex flex-col gap-1" data-testid="health-diagnostic" data-device-id={x.device_id}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-semibold dark:bg-night">{x.code}</code>
                    <span className="meta">{formatLocal(x.at, tz)}</span>
                  </div>
                  {x.message && <p className="break-words text-sm" data-testid="health-diag-message">{x.message}</p>}
                  <p className="meta">{x.device_id === deviceId ? t('syncHealth.devices.thisDevice') : (x.display_name ?? x.device_id.slice(0, 8))}{x.app_version ? ` · ${t('syncHealth.devices.appVersion', { version: x.app_version })}` : ''}</p>
                </li>
              ))}
            </ul>
            {h.diagnostics.last.length > DIAGNOSTICS_SHOWN && (
              <button className="btn" onClick={() => setAllDiag(!allDiag)} data-testid="health-diag-all">{allDiag ? t('syncHealth.showLess') : t('syncHealth.diag.showAll', { count: h.diagnostics.last.length })}</button>
            )}
          </div>

          {/* the scheduled jobs */}
          <div className="card flex flex-col gap-2" data-testid="health-cron">
            <h2 className="text-lg font-bold">{t('syncHealth.cron.title')}</h2>
            {cronCut && <p className="meta" data-testid="health-cron-cut">{t('syncHealth.cron.cut', cronCut)}</p>}
            {h.cron_failures.length === 0 ? <p className="meta">{t('syncHealth.cron.none')}</p> : h.cron_failures.map((f, i) => (
              <div key={`${f.start_time}-${i}`} className="flex flex-col gap-0.5 border-t border-slate-200 pt-2 first:border-0 first:pt-0 dark:border-night-line" data-testid="health-cron-failure">
                <span className="flex flex-wrap items-center gap-2"><Pill tone="red">{t('syncHealth.cron.failed')}</Pill><span className="font-semibold">{f.job ?? '?'}</span><span className="meta">{formatLocal(f.start_time, tz)}</span></span>
                {f.message && <span className="break-words meta">{f.message}</span>}
              </div>
            ))}
          </div>

          {/* the backup */}
          <div className="card flex flex-col gap-2" data-testid="health-backup">
            <h2 className="text-lg font-bold">{t('syncHealth.backup.title')}</h2>
            {h.backup.last_ok_at
              ? <p>{t('syncHealth.backup.last', { at: formatLocal(h.backup.last_ok_at, tz), hours: fmtNum(h.backup.age_hours ?? 0, lang) })}</p>
              : <p>{t('syncHealth.backup.none')}</p>}
            {!h.backup.expected && <p className="meta">{t('syncHealth.backup.notExpected')}</p>}
            {backupOverdue(h.backup) && <Banner tone="error">{t('syncHealth.backup.overdue', { hours: BACKUP_OVERDUE_HOURS })}</Banner>}
            {h.backup.last_failure_at && <p className="meta">{t('syncHealth.backup.failure', { at: formatLocal(h.backup.last_failure_at, tz) })}</p>}
          </div>
        </>
      )}
    </section>
  )
}

function CommandsBlock({ w, t, lang }: { w: CommandWindow; t: T; lang: string }) {
  const problems = problemRows(w)
  const taken = takenPercent(w)
  return (
    <>
      <p data-testid="health-total">{t('syncHealth.commands.total', { count: w.total })}{taken !== null ? ` · ${t('syncHealth.commands.taken', { pct: taken })}` : ''}</p>
      <div className="flex flex-wrap gap-2" data-testid="health-outcomes">
        {OUTCOME_ORDER.filter((s) => statusCount(w, s) > 0).map((s) => <span key={s} className="flex items-center gap-1"><StatusPill status={s} /><span className="text-sm tabular-nums">{statusCount(w, s)}</span></span>)}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm" data-testid="health-latency">
        <dt className="font-semibold">{t('syncHealth.commands.p50')}</dt><dd>{dur(t, w.latency_ms.p50, lang)}</dd>
        <dt className="font-semibold">{t('syncHealth.commands.p90')}</dt><dd>{dur(t, w.latency_ms.p90, lang)}</dd>
        <dt className="font-semibold">{t('syncHealth.commands.p99')}</dt><dd>{dur(t, w.latency_ms.p99, lang)}</dd>
        <dt className="font-semibold">{t('syncHealth.commands.max')}</dt><dd>{dur(t, w.latency_ms.max, lang)}</dd>
      </dl>
      <p className="meta">{t('syncHealth.commands.latencyHelp')}</p>
      {problems.length > 0 ? (
        <div className="flex flex-col gap-1" data-testid="health-problems">
          <h3 className="font-semibold">{t('syncHealth.commands.problems')}</h3>
          {problems.map((r) => (
            <div key={`${r.name}|${r.status}`} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">{commandLabel(t, r.name)}</span>
              <span className="flex shrink-0 items-center gap-2"><StatusPill status={r.status} /><span className="tabular-nums">{r.n}</span></span>
            </div>
          ))}
        </div>
      ) : w.total > 0 && <p className="meta">{t('syncHealth.commands.noProblem')}</p>}
    </>
  )
}

function DeviceCard({ d, mine, t, lang, tz }: { d: HealthDevice; mine: boolean; t: T; lang: string; tz: string }) {
  return (
    <div className={`card flex flex-col gap-2 ${d.pending_over_24h ? 'border-red-400 dark:border-red-700' : ''}`} data-testid="health-device" data-device-id={d.device_id} data-this-device={mine ? '1' : '0'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 truncate font-semibold">{deviceName(d)}</span>
        <span className="flex flex-wrap gap-1">
          {mine && <Pill tone="green">{t('syncHealth.devices.thisDevice')}</Pill>}
          <Pill tone="gray">{t(d.kind === 'kiosk' ? 'syncHealth.devices.kiosk' : 'syncHealth.devices.personal')}</Pill>
          {d.pending_over_24h && <Pill tone="red"><span data-testid="health-pending-old">{t('syncHealth.devices.pendingOld')}</span></Pill>}
        </span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="font-semibold">{t('syncHealth.devices.lastSeen')}</dt><dd>{d.last_seen_at ? formatLocal(d.last_seen_at, tz) : t('syncHealth.devices.never')}</dd>
        <dt className="font-semibold">{t('syncHealth.devices.outbox')}</dt><dd>{d.outbox_depth === null ? '—' : t('syncHealth.devices.waiting', { count: d.outbox_depth })}{d.oldest_pending_at && (d.outbox_depth ?? 0) > 0 ? ` · ${t('syncHealth.devices.oldest', { at: formatLocal(d.oldest_pending_at, tz) })}` : ''}</dd>
        <dt className="font-semibold">{t('syncHealth.devices.clock')}</dt><dd>{dur(t, d.clock_offset_ms, lang)}</dd>
        <dt className="font-semibold">{t('syncHealth.devices.app')}</dt><dd>{d.app_version ?? '—'}</dd>
      </dl>
      <div className="flex flex-wrap gap-1">
        {d.storage_persisted !== null && <Pill tone={d.storage_persisted ? 'green' : 'amber'}>{t(d.storage_persisted ? 'syncHealth.devices.persisted' : 'syncHealth.devices.notPersisted')}</Pill>}
        {d.standalone !== null && <Pill tone="gray">{t(d.standalone ? 'syncHealth.devices.standalone' : 'syncHealth.devices.browser')}</Pill>}
      </div>
    </div>
  )
}
